import {
  Order,
  OrderActionTypes,
  OrderAlertLevel,
  OrderDestination,
  OrderDestinationStatus,
  OrderDriverStatus,
  OrderEventTypes,
  OrderOverallStatus,
} from '@hero/schema';
import { CONFIG } from '../config';

const {
  statusActivityConfig,
  actionConfig,
  eventsConfig,
  alertConfig,
  carrierNextStepConfig,
  orderFieldsConfig,
} = CONFIG.order;

// * status activity
export type DestinationStatusCheck = keyof typeof statusActivityConfig.destination;
export type DriverStatusCheck = keyof typeof statusActivityConfig.driver;
export type OrderStatusCheck = keyof typeof statusActivityConfig.order;

export const isDestinationStatus = (
  type: DestinationStatusCheck,
  status: OrderDestinationStatus,
) => {
  const result = statusActivityConfig.destination[type].indexOf(status);
  // if -1 means it did not found the status
  return result !== -1;
};
export const isOrderStatus = (type: OrderStatusCheck, status: OrderOverallStatus) => {
  const result = statusActivityConfig.order[type].indexOf(status);
  // if -1 means it did not found the status
  return result !== -1;
};
export const isDriverStatus = (type: DriverStatusCheck, status: OrderDriverStatus) => {
  const result = statusActivityConfig.driver[type].indexOf(status);
  // if -1 means it did not found the status
  return result !== -1;
};
/** returns the current active Destination based on its status */
export const getActiveDestination = (destinations: Order['destinations']) => {
  let index = -1;
  const destination = destinations.find((des, i) => {
    const isActive = isDestinationStatus('active', des.status);
    if (isActive) {
      index = i;
      return des;
    }
    return;
  });
  return { destination, index };
};

export const getNextDestination = (destinations: Order['destinations']) => {
  let destination: OrderDestination | undefined;
  let index = -1;
  const active = getActiveDestination(destinations);
  if (active.index !== -1) {
    const nextIndex = active.index + 1;
    destination = destinations[nextIndex];
    if (destination) {
      index = nextIndex;
    }
  }
  return { index, destination };
};
export const isActiveDestinationFirst = (destinations: Order['destinations']) => {
  const { index } = getActiveDestination(destinations);
  return index === 0;
};

export const isActiveDestinationLast = (destinations: Order['destinations']) => {
  const { index } = getActiveDestination(destinations);
  const lastDestinationIndex = destinations.length - 1;
  return index === lastDestinationIndex;
};
export const getExpectedDestinationArrivalDate = (order: Order) => {
  return order.dates.pickup.from;
};

//* events
export const getOrderActionConfig = (action: OrderActionTypes) => {
  return actionConfig[action];
};

export const getEventConfig = (event: OrderEventTypes) => {
  return eventsConfig[event];
};

//* timeline
export const findOrderEventInTimeline = (
  timeline: Order['timeline'],
  eventName: OrderEventTypes,
) => {
  const event = timeline.find(t => t.event === eventName);
  return event;
};
export const findOrderDestinationEventInTimeline = (
  eventName: OrderEventTypes,
  destinationId: string | undefined,
  timeline: Order['timeline'],
) => {
  const event = timeline.find(e => {
    if (e.event === eventName && destinationId === e.destinationId) {
      return e;
    }
    return;
  });
  return event;
};
export const getOrderTimelineForCustomer = (order: Order) => {
  const getEvent = (eventName: OrderEventTypes, destinationId?: string) => {
    const timeline = order.timeline || [];
    const event = destinationId
      ? findOrderDestinationEventInTimeline(eventName, destinationId, timeline)
      : findOrderEventInTimeline(timeline, eventName);
    return {
      ...event,
      /** if the event has occurred or not */
      occurred: event !== undefined,
    };
  };
  const destinations = order.destinations || [];
  const pickupId = destinations.find(d => d.type === 'pickup')?.id;
  const dropoffId = destinations.find(d => d.type === 'dropoff')?.id;
  return {
    orderCreated: getEvent('order_created'),

    arrivedAtPickup: getEvent('arrived', pickupId),

    /**
     * heading to the drop off Destination.
     *
     * this is the time when the carrier clicked on in_transit to drop off
     * */
    pickedUp: getEvent('in_transit', dropoffId),

    arrivedAtDropoff: getEvent('arrived', dropoffId),

    dropOff: getEvent('items_dropped_off'),

    completed: getEvent('delivered'),

    canceled: getEvent('customer_canceled_order') || getEvent('super_admin_canceled_order'),
  };
};
export type OrderTimelineForCustomer = ReturnType<typeof getOrderTimelineForCustomer>;

/**
 * this function tells if you can add an event to the timeline or not based on the `eventsConfig`
 *
 * @param timeline order timeline array
 * @param newEvent the new event to be added
 */
export const canAddNewEventToTimeline = (
  timeline: Order['timeline'],
  newEvent: OrderEventTypes,
) => {
  const allowDuplicateEvent = CONFIG.order.eventsConfig[newEvent].allowDuplicate;
  if (allowDuplicateEvent) return true;

  const eventAlreadyInTimeline = timeline.find(t => t.event === newEvent);
  if (eventAlreadyInTimeline) return false;
  return true;
};

//* alert
/**
 *
 * @param alertLevel the order alert level
 * @returns the alert config such as the color needed to be displayed for the alert level
 */
export const getOrderAlertDetails = (alertLevel: OrderAlertLevel) => {
  return alertConfig.listObj[alertLevel];
};

/**
 * this function returns the next action to be taken by driver based on the order status.
 *
 * @param orderStatus the order status
 * @returns the next action to be taken based on the order status
 */
export const getOrderNextAction = (orderStatus: Order['status']['order']) => {
  return carrierNextStepConfig[orderStatus];
};

//*
export const isOrderFieldEditable = (
  field: keyof typeof orderFieldsConfig,
  orderStatus: Order['status']['order'],
) => {
  const config = orderFieldsConfig[field];
  const isDisabled = config.disableEditsInStatus.indexOf(orderStatus) !== -1;
  return !isDisabled;
};
